import { cn } from '@@/lib/utils';
import { CampaignType } from '../../types';
import { ReactNode } from 'react';

const CAMPAIGN_TYPES: CampaignType[] = [
  CampaignType.SURVEY_VOICE,
  CampaignType.NOTIFICATION_VOICE,
  CampaignType.SURVEY_SMS,
  CampaignType.NOTIFICATION_SMS
];

const CAMPAIGN_TYPE_CONFIG: Record<CampaignType, { title: string; description: string; icon: ReactNode }> = {
  [CampaignType.SURVEY_VOICE]: {
    title: 'Voice Survey',
    description:
      'Collect feedback through interactive voice calls with up ' + 'to 5 questions. Responses are entered via keypad.',
    icon: <VoiceSurveyIcon />
  },
  [CampaignType.NOTIFICATION_VOICE]: {
    title: 'Voice Notification',
    description:
      'Send automated voice calls with pre-recorded or ' +
      'text-to-speech messages. Perfect for quick updates and alerts.',
    icon: <VoiceNotificationIcon />
  },
  [CampaignType.SURVEY_SMS]: {
    title: 'SMS Survey',
    description:
      'Gather insights with interactive text surveys of up to ' + '5 questions. Responses are sent back via SMS.',
    icon: <SmsSurveyIcon />
  },
  [CampaignType.NOTIFICATION_SMS]: {
    title: 'SMS Notification',
    description: 'Send one-way text messages directly to mobile devices. ' + 'Ideal for reminders and announcements.',
    icon: <SmsNotificationIcon />
  }
};

export function CampaignTypeCard(props: { type: CampaignType; isSelected: boolean; onSelect: () => void }) {
  const config = CAMPAIGN_TYPE_CONFIG[props.type];

  return (
    <button
      type='button'
      onClick={props.onSelect}
      className={cn(
        'relative flex flex-col items-start p-5 rounded-xl',
        'border-2 text-left transition-all bg-card',
        'hover:border-blue-800 hover:shadow-sm',
        props.isSelected ? 'border-blue shadow-sm' : 'border-border'
      )}
    >
      {props.isSelected && (
        <div
          className='absolute top-3 right-3 w-5 h-5 bg-blue
            rounded-full flex items-center justify-center'
        >
          <svg className='w-3 h-3 text-white' fill='currentColor' viewBox='0 0 20 20'>
            <path
              fillRule='evenodd'
              d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0
                01-1.414 0l-4-4a1 1 0 011.414-1.414L8
                12.586l7.293-7.293a1 1 0 011.414 0z'
              clipRule='evenodd'
            />
          </svg>
        </div>
      )}
      <div
        className='w-12 h-12 rounded-lg bg-blue-500 flex items-center
          justify-center mb-4'
      >
        {config.icon}
      </div>
      <h3 className='text-base font-semibold text-foreground mb-2'>{config.title}</h3>
      <p className='text-sm text-muted-foreground leading-relaxed'>{config.description}</p>
    </button>
  );
}

function VoiceSurveyIcon() {
  return (
    <svg className='w-6 h-6 text-blue' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0
          0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3
          3 0 01-3 3z'
      />
    </svg>
  );
}

function VoiceNotificationIcon() {
  return (
    <svg className='w-6 h-6 text-blue' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 10h16M4 14h16M4 18h16' />
    </svg>
  );
}

function SmsSurveyIcon() {
  return (
    <svg className='w-6 h-6 text-blue' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0
          012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0
          01.293.707V19a2 2 0 01-2 2z'
      />
    </svg>
  );
}

function SmsNotificationIcon() {
  return (
    <svg className='w-6 h-6 text-blue' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0
          01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0
          01-2 2h-5l-5 5v-5z'
      />
    </svg>
  );
}

export function SelectTypeStep(props: {
  selectedType: CampaignType | null;
  onSelectType: (type: CampaignType) => void;
}) {
  return (
    <div>
      <h2 className='text-xl font-semibold text-foreground mb-6'>Select Campaign Type</h2>
      <div className='grid grid-cols-2 gap-4'>
        {CAMPAIGN_TYPES.map((type) => (
          <CampaignTypeCard
            key={type}
            type={type}
            isSelected={props.selectedType === type}
            onSelect={() => props.onSelectType(type)}
          />
        ))}
      </div>
    </div>
  );
}
